<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();

const props = defineProps({
  conversationAdditionalAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const INITIAL_DISPLAY_LIMIT = 1;
const searchTerm = ref('');
const showAll = ref(false);

const participants = computed(() => {
  return props.conversationAdditionalAttributes?.group_conversation_participants || [];
});

const filteredParticipants = computed(() => {
  if (!searchTerm.value) {
    return participants.value;
  }

  const lowerCaseSearchTerm = searchTerm.value.toLowerCase();
  
  return participants.value.filter(p => {
    const nameMatch = p.name?.toLowerCase().includes(lowerCaseSearchTerm) ?? false;
    const phoneMatch = p.phone?.includes(lowerCaseSearchTerm) ?? false;
    return nameMatch || phoneMatch;
  });
});

const totalCount = computed(() => filteredParticipants.value.length);

const participantsToDisplay = computed(() => {
  if (showAll.value || searchTerm.value) {
    return filteredParticipants.value;
  }
  return filteredParticipants.value.slice(0, INITIAL_DISPLAY_LIMIT);
});

const hasMoreToShow = computed(() => (
  totalCount.value > INITIAL_DISPLAY_LIMIT && !showAll.value && !searchTerm.value
));

const handleSearch = (event) => {
  const value = event.target.value;
  searchTerm.value = value;
  if (value) {
    showAll.value = true;
  } else {
    showAll.value = false;
  }
};
</script>

<template>
  <div class="list-group-item--content">
    
    <div class="p-3 border-b border-slate-50 dark:border-slate-700">
      <input
        type="text"
        :value="searchTerm"
        @input="handleSearch"
        :placeholder="$t('CONVERSATION_SIDEBAR.ACCORDION.SEARCH_BUTTON')"
        class="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-150 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
        aria-label="Buscar participantes"
      />
    </div>

    <div v-if="participantsToDisplay.length" class="p-3">
      <ul class="participants-list divide-y divide-slate-50 dark:divide-slate-700">
        <li
          v-for="p in participantsToDisplay"
          :key="p.id || p.phone"
          class="flex flex-col py-2"
        >
          <a
            :href="`/app/accounts/${route.params.accountId}/contacts/${p.id}`"
            target="_blank"
            class="text-blue-500 hover:text-blue-600 transition duration-150 ease-in-out font-medium mb-1"
          >
            {{ p.name }}
          </a>

          <span class="text-xs text-slate-500 dark:text-slate-400">
            {{ p.phone }}
          </span>
        </li>
      </ul>
    </div>
    
    <div v-else class="p-4 text-slate-500 dark:text-slate-400 text-center">
      <span v-if="searchTerm">
        {{ $t('CONVERSATION_SIDEBAR.ACCORDION.SEARCH_EMPTY_STATE_TITLE') }}
      </span>
      <span v-else>
        {{ $t('CONVERSATION_SIDEBAR.ACCORDION.NO_PARTICIPANTS') }}
      </span>
    </div>

    <div class="p-3 border-t border-slate-50 dark:border-slate-700">
      <button
        v-if="hasMoreToShow"
        @click="showAll = true"
        class="w-full bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 font-semibold py-2 px-4 rounded-lg border border-blue-200 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-slate-700 transition duration-200"
      >
        {{ $t('CONVERSATION_SIDEBAR.ACCORDION.BUTTON_SEE_MORE', { count: (totalCount - INITIAL_DISPLAY_LIMIT)}) }}
      </button>

      <button
        v-if="!searchTerm && showAll && totalCount > INITIAL_DISPLAY_LIMIT"
        @click="showAll = false"
        class="w-full mt-2 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-semibold py-2 px-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition duration-200"
      >
        {{ $t('CONVERSATION_SIDEBAR.ACCORDION.BUTTON_SEE_LESS') }}
      </button>
    </div>

  </div>
</template>
